import { useAtom } from "jotai"
import { atomWithStorage } from "jotai/utils"
import { useEffect } from "react"
import {
  coalesceClickIdentifiers,
  mergeClickIdentifiers,
  type GoogleAdsClickIdentifier,
  type GoogleAdsStoredConsent,
} from "@shared/helpers/googleAdsClickId"

export interface AnalyticsData {
  gaClientId?: string
  gaSessionId?: number
  gaSessionNumber?: number
  source: string | null
  medium: string | null
  campaign: string | null
  term: string | null
  content: string | null
  metaEventId?: string
  fbp?: string
  fbc?: string
  gclid?: string
  gbraid?: string
  wbraid?: string
  clickIdentifiers?: GoogleAdsClickIdentifier[]
  consent?: GoogleAdsStoredConsent
}

export const analyticsAtom = atomWithStorage<AnalyticsData | null>(
  "analytics",
  null
)

let hookInstanceCount = 0 // Globální počítadlo instancí

const CONSENT_KEYS = [
  "analytics_storage",
  "ad_storage",
  "ad_user_data",
  "ad_personalization",
] as const

function readConsentValue(value: unknown): "granted" | "denied" | undefined {
  if (value === true || value === "granted") return "granted"
  if (value === false || value === "denied") return "denied"
  return undefined
}

/** Only an explicit Consent Mode update. Defaults are not treated as a choice. */
function readExplicitGoogleConsent(): GoogleAdsStoredConsent | undefined {
  if (typeof window === "undefined") return undefined

  const fromTag: GoogleAdsStoredConsent = {}
  const entries = (window as any).google_tag_data?.ics?.entries
  if (entries) {
    for (const key of CONSENT_KEYS) {
      const parsed = readConsentValue(entries[key]?.update)
      if (parsed) fromTag[key] = parsed
    }
  }
  if (Object.keys(fromTag).length > 0) return fromTag

  const dataLayer = (window as any).dataLayer
  if (!Array.isArray(dataLayer)) return undefined
  const fromLayer: GoogleAdsStoredConsent = {}
  for (const entry of dataLayer) {
    if (!Array.isArray(entry) || entry[0] !== "consent" || entry[1] !== "update") {
      continue
    }
    const payload = entry[2]
    if (!payload || typeof payload !== "object") continue
    for (const key of CONSENT_KEYS) {
      const parsed = readConsentValue(payload[key])
      if (parsed) fromLayer[key] = parsed
    }
  }
  return Object.keys(fromLayer).length > 0 ? fromLayer : undefined
}

export function useTrafficSource() {
  const [analytics, setAnalytics] = useAtom(analyticsAtom)

  useEffect(() => {
    if (typeof window === "undefined") return

    hookInstanceCount++
    const isPrimaryInstance = hookInstanceCount === 1 // Logiku spustí jen první instance

    if (!isPrimaryInstance) {
      hookInstanceCount--
      return
    }

    const getCookie = (name: string) => {
      try {
        const v = document.cookie.match("(^|;) ?" + name + "=([^;]*)(;|$)")
        return v ? v[2] : null
      } catch (e) {
        return null
      }
    }

    const parseGa4SessionFromCookie = (cookieValue: string | null) => {
      if (!cookieValue)
        return { gaSessionId: undefined, gaSessionNumber: undefined }

      // New GA4 cookie format (GS2...): contains s<sessionId> and o<sessionNumber>
      const sidMatch = cookieValue.match(/s(\d{9,})/)
      const snMatch = cookieValue.match(/o(\d{1,})/)

      const sid = sidMatch ? Number(sidMatch[1]) : undefined
      const sn = snMatch ? Number(snMatch[1]) : undefined

      return {
        gaSessionId: Number.isFinite(sid) ? sid : undefined,
        gaSessionNumber: Number.isFinite(sn) ? sn : undefined,
      }
    }

    const checkCookiesAndUpdate = () => {
      const url = new URL(window.location.href)
      const params = url.searchParams

      let newSource = params.get("utm_source")
      let newMedium = params.get("utm_medium")
      const newCampaign = params.get("utm_campaign")
      const newTerm = params.get("utm_term")
      const newContent = params.get("utm_content")

      const newGclid = params.get("gclid")
      const newGbraid = params.get("gbraid")
      const newWbraid = params.get("wbraid")
      const newSrsltid = params.get("srsltid")
      const newGadSource = params.get("gad_source")
      const newFbclid = params.get("fbclid")
      const newEcmid = params.get("ecmid")
      const newRef = params.get("ref")
      const newFrom = params.get("from")
      const hasSklikParam = params.has("sklik")

      if (newEcmid && !newSource) {
        newSource = "ecomail"
        if (!newMedium) newMedium = "email"
      }

      if ((newGclid || newGbraid || newWbraid || newGadSource) && !newSource) {
        newSource = "google"
        if (!newMedium) newMedium = "cpc"
      }

      if (newSrsltid && !newSource) {
        newSource = "google"
        if (!newMedium) newMedium = "organic_shopping"
      }

      if (newFbclid && !newSource) {
        newSource = "facebook"
        if (!newMedium) newMedium = "social"
      }

      if (
        (newRef === "sklik" || newFrom === "sklik" || hasSklikParam) &&
        !newSource
      ) {
        newSource = "seznam"
        if (!newMedium) newMedium = "cpc"
      }

      const gaCookie = getCookie("_ga")
      let gaClientId: string | undefined = undefined
      if (gaCookie) {
        const parts = gaCookie.split(".")
        if (parts.length >= 4) gaClientId = parts.slice(2).join(".")
      }

      const ga4MeasurementId = process.env.GATSBY_GA4_MEASUREMENT_ID || ""
      const gaCookieName = ga4MeasurementId
        ? "_ga_" + ga4MeasurementId.replace("G-", "").replace(/-/g, "")
        : null

      const ga4Cookie = gaCookieName ? getCookie(gaCookieName) : null
      const { gaSessionId, gaSessionNumber } =
        parseGa4SessionFromCookie(ga4Cookie)

      const fbpCookie = getCookie("_fbp")
      const fbcCookie = getCookie("_fbc")
      const fbcDerived =
        fbcCookie ||
        (newFbclid ? `fb.1.${Date.now()}.${newFbclid}` : undefined)
      const explicitConsent = readExplicitGoogleConsent()
      const observedClicks = [
        newGclid ? { type: "gclid" as const, value: newGclid } : null,
        newGbraid ? { type: "gbraid" as const, value: newGbraid } : null,
        newWbraid ? { type: "wbraid" as const, value: newWbraid } : null,
      ].filter(
        (entry): entry is { type: "gclid" | "gbraid" | "wbraid"; value: string } =>
          !!entry
      )

      setAnalytics(prev => {
        const val = (v: string | null | undefined) => v || null
        const mergedClicks = mergeClickIdentifiers({
          existing: coalesceClickIdentifiers(prev),
          observed: observedClicks,
          observedAt: new Date(),
          source: newSource || prev?.source || null,
          medium: newMedium || prev?.medium || null,
          consent: explicitConsent ?? prev?.consent,
        })
        const clickIdentifiersChanged =
          mergedClicks.changed ||
          (mergedClicks.identifiers.length > 0 && !prev?.clickIdentifiers)
        const consentChanged =
          !!explicitConsent &&
          (explicitConsent.analytics_storage !== prev?.consent?.analytics_storage ||
            explicitConsent.ad_storage !== prev?.consent?.ad_storage ||
            explicitConsent.ad_user_data !== prev?.consent?.ad_user_data ||
            explicitConsent.ad_personalization !== prev?.consent?.ad_personalization)

        const hasNewCampaign =
          newSource ||
          newMedium ||
          newCampaign ||
          newGclid ||
          newGbraid ||
          newWbraid ||
          newSrsltid ||
          newFbclid ||
          newEcmid ||
          newRef ||
          newFrom ||
          hasSklikParam

        const newGaIdFound = gaClientId && prev?.gaClientId !== gaClientId
        const newGaSessionFound =
          (gaSessionId && prev?.gaSessionId !== gaSessionId) ||
          (gaSessionNumber && prev?.gaSessionNumber !== gaSessionNumber)
        const newFbpFound = fbpCookie && prev?.metaEventId !== fbpCookie
        const newFbcFound = fbcDerived && prev?.fbc !== fbcDerived

        const nextClickFields = {
          gclid: newGclid || prev?.gclid,
          gbraid: newGbraid || prev?.gbraid,
          wbraid: newWbraid || prev?.wbraid,
          clickIdentifiers: mergedClicks.identifiers,
          consent: explicitConsent ?? prev?.consent,
        }

        if (!prev) {
          return {
            gaClientId,
            gaSessionId,
            gaSessionNumber,
            source: val(newSource),
            medium: val(newMedium),
            campaign: val(newCampaign),
            term: val(newTerm),
            content: val(newContent),
            metaEventId: fbpCookie || undefined,
            fbp: fbpCookie || undefined,
            fbc: fbcDerived || undefined,
            ...nextClickFields,
          }
        }

        if (
          hasNewCampaign ||
          newGaIdFound ||
          newGaSessionFound ||
          newFbpFound ||
          newFbcFound ||
          clickIdentifiersChanged ||
          consentChanged
        ) {
          return {
            ...prev,
            gaClientId: gaClientId || prev.gaClientId,
            gaSessionId: gaSessionId || prev.gaSessionId,
            gaSessionNumber: gaSessionNumber || prev.gaSessionNumber,
            source: newSource || prev.source,
            medium: newMedium || prev.medium,
            campaign: newCampaign || prev.campaign,
            term: newTerm || prev.term,
            content: newContent || prev.content,
            metaEventId: fbpCookie || prev.metaEventId,
            fbp: fbpCookie || prev.fbp,
            fbc: fbcDerived || prev.fbc,
            ...nextClickFields,
          }
        }

        return prev
      })

      return !!gaClientId
    }

    // Run immediately
    // checkCookiesAndUpdate() // <-- Removed immediate call to avoid initial double-log confusion if interval starts fast, or just keep it.
    // Actually, keep it but maybe log it differently.
    checkCookiesAndUpdate()

    // 1. Interval: Check every 2s indefinitely (cheap operation)
    const interval = setInterval(() => {
      checkCookiesAndUpdate()
    }, 3000)

    // 2. Interaction: Check on any click (User likely clicked "Accept Cookies")
    const clickHandler = () => checkCookiesAndUpdate()
    document.addEventListener("click", clickHandler)

    return () => {
      clearInterval(interval)
      document.removeEventListener("click", clickHandler)
      hookInstanceCount--
    }
  }, [setAnalytics])
}
